/**
 * Dynamic Forms Service
 * 
 * This service handles all dynamic forms operations including departments, 
 * categories, forms, and form submissions using modern REST endpoints.
 * 
 * ✅ HEALTHCARE COMPLIANCE: This service now includes client-side awareness
 * of PHI encryption requirements and validation.
 */

import apiClient from './apiClient';
import formTemplateService from './formTemplateService';

const unwrap = (resp) => {
    // Handle SuccessResponse envelope { success, message, data, meta }
    if (resp && resp.success !== undefined && resp.data !== undefined) {
        return resp;
    }
    // Axios style – backend SuccessResponse is in resp.data
    const payload = resp?.data || {};
    if (payload && payload.success !== undefined) {
        return payload;
    }
    // Fallback – return as-is
    return { success: true, data: payload };
};

class DynamicFormsService {
    constructor() {
        this.baseURL = '/dynamic-forms';
        
        // ✅ HEALTHCARE COMPLIANCE: PHI-sensitive field types for client-side validation
        this.phiSensitiveFieldTypes = new Set([
            'text', 'textarea', 'email', 'select', 'selectManual'
        ]);
        
        // Keywords that suggest PHI content (for client-side warnings)
        this.phiKeywords = new Set([
            'name', 'first_name', 'last_name', 'phone', 'email', 'address',
            'diagnosis', 'medication', 'patient', 'clinical', 'medical'
        ]);

        // 🚀 PERFORMANCE OPTIMIZATION: Add caching and batching
        this.cache = new Map();
        this.cacheTTL = 5 * 60 * 1000; // 5 minutes
        this.pendingRequests = new Map(); // Prevent duplicate simultaneous requests
        this.batchQueue = new Map(); // Queue for batching related requests

        // 🗺️ FIELD MAP CACHING: Cross-version field ID resolution
        this.fieldMapCache = new Map();
        this.fieldMapCacheTTL = 30 * 60 * 1000; // 30 minutes (longer TTL for field maps)
    }

    // Normalize version param: preserve existing formats for backward compatibility
    sanitizeVersion(version) {
        if (version === null || version === undefined || version === '') return null;
        if (typeof version === 'number') return version;
        if (typeof version === 'string') {
            const trimmed = version.trim();
            // Keep existing formats as-is to maintain backward compatibility
            // This allows both "1.0" (legacy) and "v1" (new) formats to work
            return trimmed;
        }
        return version;
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Get cached data or return null if expired
     */
    getCachedData(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
            return cached.data;
        }
        return null;
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Set cached data with timestamp
     */
    setCachedData(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending
     */
    isRequestPending(key) {
        return this.pendingRequests.has(key);
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Global request deduplication
     * This prevents any duplicate requests across the entire application
     */
    static globalPendingRequests = new Map();
    
    static isGlobalRequestPending(key) {
        return DynamicFormsService.globalPendingRequests.has(key);
    }
    
    static setGlobalRequestPending(key, promise) {
        DynamicFormsService.globalPendingRequests.set(key, promise);
        promise.finally(() => DynamicFormsService.globalPendingRequests.delete(key));
        return promise;
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Mark request as pending
     */
    setRequestPending(key, promise) {
        this.pendingRequests.set(key, promise);
        promise.finally(() => this.pendingRequests.delete(key));
        return promise;
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Batch multiple related API calls
     */
    async batchRequests(requests) {
        const results = {};
        const promises = [];

        for (const [key, requestFn] of Object.entries(requests)) {
            const cacheKey = `batch_${key}`;
            const cached = this.getCachedData(cacheKey);
            
            if (cached) {
                results[key] = cached;
            } else {
                const promise = requestFn().then(data => {
                    this.setCachedData(cacheKey, data);
                    return data;
                });
                promises.push(promise);
                results[key] = promise;
            }
        }

        // Wait for all pending requests to complete
        if (promises.length > 0) {
            await Promise.all(promises);
        }

        // Resolve all results
        for (const [key, result] of Object.entries(results)) {
            if (result instanceof Promise) {
                results[key] = await result;
            }
        }

        return results;
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Clear expired cache entries
     */
    clearExpiredCache() {
        const now = Date.now();
        for (const [key, cached] of this.cache.entries()) {
            if (now - cached.timestamp > this.cacheTTL) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Clear all cache
     */
    clearCache() {
        this.cache.clear();
        this.pendingRequests.clear();
        this.batchQueue.clear();
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Clear only forms-related cache
     */
    clearFormsCache() {
        // Clear cache keys that start with 'forms_' (lists) or 'form_' (individual forms)
        const keysToRemove = [];
        for (const key of this.cache.keys()) {
            if (key.startsWith('forms_') || key.startsWith('form_')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => this.cache.delete(key));

        // Clear pending requests for forms
        const pendingKeysToRemove = [];
        for (const key of this.pendingRequests.keys()) {
            if (key.startsWith('forms_') || key.startsWith('form_')) {
                pendingKeysToRemove.push(key);
            }
        }
        pendingKeysToRemove.forEach(key => this.pendingRequests.delete(key));

        // Clear global pending requests for forms
        const globalKeysToRemove = [];
        for (const key of DynamicFormsService.globalPendingRequests.keys()) {
            if (key.startsWith('forms_') || key.startsWith('form_')) {
                globalKeysToRemove.push(key);
            }
        }
        globalKeysToRemove.forEach(key => DynamicFormsService.globalPendingRequests.delete(key));

        console.log('🚀 Forms cache cleared (including individual forms)');
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Clear cache for a specific form (all versions)
     * This is useful when a form version is changed/activated
     */
    clearFormCache(formId) {
        // Clear all cache keys that include this formId (including all versions)
        const keysToRemove = [];
        for (const key of this.cache.keys()) {
            if (key.includes(`form_${formId}`)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => this.cache.delete(key));

        // Clear pending requests for this form
        const pendingKeysToRemove = [];
        for (const key of this.pendingRequests.keys()) {
            if (key.includes(`form_${formId}`)) {
                pendingKeysToRemove.push(key);
            }
        }
        pendingKeysToRemove.forEach(key => this.pendingRequests.delete(key));

        // Clear global pending requests for this form
        const globalKeysToRemove = [];
        for (const key of DynamicFormsService.globalPendingRequests.keys()) {
            if (key.includes(`form_${formId}`)) {
                globalKeysToRemove.push(key);
            }
        }
        globalKeysToRemove.forEach(key => DynamicFormsService.globalPendingRequests.delete(key));

        console.log(`🗑️  Cleared cache for form ${formId} (all versions)`);
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Clear global cache
     */
    static clearGlobalCache() {
        DynamicFormsService.globalPendingRequests.clear();
        console.log('🚀 Global cache cleared');
    }
    
    /**
     * ✅ HEALTHCARE COMPLIANCE: Check if form field likely contains PHI
     * This helps the frontend show appropriate warnings to users.
     */
    isPotentialPhiField(field) {
        if (!this.phiSensitiveFieldTypes.has(field.field_type)) {
            return false;
        }
        
        const labelLower = (field.label || '').toLowerCase();
        return Array.from(this.phiKeywords).some(keyword => labelLower.includes(keyword));
    }
    
    /**
     * ✅ HEALTHCARE COMPLIANCE: Validate form submission for PHI awareness
     * This adds client-side validation before sending sensitive data.
     */
    validatePhiCompliance(formData, formDefinition) {
        const warnings = [];
        const phiFields = [];
        
        if (formDefinition && formDefinition.fields) {
            formDefinition.fields.forEach(field => {
                if (this.isPotentialPhiField(field)) {
                    phiFields.push(field.label);
                    
                    // Check if field has data
                    const fieldData = formData.find(d => d.field_id === field.field_id);
                    if (fieldData && fieldData.value) {
                        warnings.push(`Field "${field.label}" may contain PHI and will be encrypted`);
                    }
                }
            });
        }
        
        return {
            hasPhiFields: phiFields.length > 0,
            phiFields,
            warnings,
            compliant: true // Backend handles encryption
        };
    }

    // ============================================================================
    // FORM MANAGEMENT
    // ============================================================================

    /**
     * Get list of forms with filtering and pagination
     */
    async getForms(params = {}, forceRefresh = false) {
        try {
            console.log('🎯 dynamicFormsService.getForms called with params:', params);
            console.log('🎯 forceRefresh:', forceRefresh);

            // 🚀 PERFORMANCE OPTIMIZATION: Check cache first (unless force refresh is requested)
            // IMPORTANT: Disable cache for pagination requests to ensure fresh data
            const isPaginationRequest = params.skip !== undefined || params.limit !== undefined || params.page !== undefined;
            const cacheKey = `forms_${JSON.stringify(params)}`;
            console.log('🗄️ Cache key:', cacheKey);
            console.log('🗄️ Is pagination request:', isPaginationRequest);

            if (!forceRefresh && !isPaginationRequest) {
                const cached = this.getCachedData(cacheKey);
                if (cached) {
                    console.log('🚀 Using cached forms data:', cached);
                    return cached;
                } else {
                    console.log('🚀 No cached data found');
                }
            } else {
                console.log('🚀 Skipping cache due to:', forceRefresh ? 'forceRefresh=true' : 'pagination request');
            }

            // 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending (local and global)
            // IMPORTANT: Skip duplicate request prevention for pagination to ensure fresh data
            if (!isPaginationRequest) {
                if (this.isRequestPending(cacheKey)) {
                    console.log('🚀 Forms request already pending locally, waiting...');
                    return await this.pendingRequests.get(cacheKey);
                }

                if (DynamicFormsService.isGlobalRequestPending(cacheKey)) {
                    console.log('🚀 Forms request already pending globally, waiting...');
                    return await DynamicFormsService.globalPendingRequests.get(cacheKey);
                }
            } else {
                console.log('🚀 Skipping duplicate request prevention for pagination request');
            }

            const apiParams = {
                ...params,
                summary: true, // Always request the lightweight version
                include_details: true // Include department and category details
            };

            console.log('🌐 Calling API with params:', apiParams);
            console.log('🌐 API URL:', `${this.baseURL}/forms`);

            const self = this; // Capture 'this' context for use in .then()
            const promise = apiClient.get(`${this.baseURL}/forms`, { params: apiParams })
                .then(response => {
                    console.log('📥 Raw API response:', response);

                    const { success, data, meta, message } = unwrap(response);
                    console.log('📥 Unwrapped response:', { success, data, meta, message });

                    const list = data?.items || data || [];
                    const total = meta?.total || data?.total || 0;

                    console.log('📊 Processed response:', {
                        listLength: list.length,
                        total,
                        firstItem: list[0],
                        lastItem: list[list.length - 1]
                    });

                    const result = { success, data: list, total, message };

                    // Only cache non-pagination requests
                    if (!isPaginationRequest) {
                        self.setCachedData(cacheKey, result);
                        console.log('✅ Caching result from getForms');
                    } else {
                        console.log('✅ Skipping cache for pagination request');
                    }
                    console.log('✅ Final result from getForms:', result);

                    return result;
                });

            // Set both local and global pending requests only for non-pagination requests
            if (!isPaginationRequest) {
                this.setRequestPending(cacheKey, promise);
                return await DynamicFormsService.setGlobalRequestPending(cacheKey, promise);
            } else {
                console.log('🚀 Skipping pending request management for pagination');
                return await promise;
            }
        } catch (error) {
            console.error('💥 Error in dynamicFormsService.getForms:', error);
            console.error('💥 Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch forms",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get form by ID
     */
    async getFormById(formId, version = null) {
        try {
            // 🚀 PERFORMANCE OPTIMIZATION: Check cache first
            const normalizedVersion = this.sanitizeVersion(version);
            const cacheKey = `form_${formId}${normalizedVersion ? `_${normalizedVersion}` : ''}`;
            const cached = this.getCachedData(cacheKey);
            if (cached) {
                console.log('🚀 Using cached form data for:', formId, normalizedVersion || '(latest)');
                return cached;
            }

            // 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending (local and global)
            if (this.isRequestPending(cacheKey)) {
                console.log('🚀 Form request already pending locally for:', formId, normalizedVersion || '(latest)');
                return await this.pendingRequests.get(cacheKey);
            }
            
            if (DynamicFormsService.isGlobalRequestPending(cacheKey)) {
                console.log('🚀 Form request already pending globally for:', formId, normalizedVersion || '(latest)');
                return await DynamicFormsService.globalPendingRequests.get(cacheKey);
            }

            const params = {};
            if (normalizedVersion) params.version = normalizedVersion;

            const promise = apiClient.get(`${this.baseURL}/forms/${formId}`, { params })
                .then(response => {
                    const result = unwrap(response);
                    this.setCachedData(cacheKey, result);
                    return result;
                });

            // Set both local and global pending requests
            this.setRequestPending(cacheKey, promise);
            return await DynamicFormsService.setGlobalRequestPending(cacheKey, promise);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form",
                data: null
            };
        }
    }

    /**
     * Create new form
     */
    async createForm(formData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/forms`, formData);
            
            // Clear forms cache to ensure fresh data after creating form
            this.clearFormsCache();
            
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create form",
                data: null
            };
        }
    }

    /**
     * Update existing form
     */
    async updateForm(formId, formData, version = null) {
        // console.log("formId", formId)
        // console.log("formData", formData)
        try {
            const params = {};
            const normalizedVersion = this.sanitizeVersion(version);
            if (normalizedVersion) params.version = normalizedVersion;
            const response = await apiClient.put(`${this.baseURL}/forms/${formId}`, formData, { params });

            // Clear forms cache to ensure fresh data after updating form
            this.clearFormsCache();

            // Clear field map cache since form structure may have changed
            this.invalidateFieldMapCache(formId);

            // Clear formTemplateService version cache
            formTemplateService.cache.delete(`version_info_${formId}`);
            formTemplateService.cache.delete(`versions_${formId}`);
            console.log('✨ Cleared version cache for form:', formId);

            // Clear localStorage cache for this form (fixes test server caching)
            localStorage.removeItem(`form_${formId}`);
            localStorage.removeItem(`version_info_${formId}`);
            localStorage.removeItem(`form_data_${formId}`);

            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update form",
                data: null
            };
        }
    }

    /**
     * Delete form (soft delete)
     */
    async deleteForm(formId) {
        try {
            const response = await apiClient.delete(`${this.baseURL}/forms/${formId}`);

            // Clear forms cache to ensure fresh data after deleting form
            this.clearFormsCache();

            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to delete form"
            };
        }
    }

    /**
     * Activate form
     */
    async activateForm(formId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/forms/${formId}/activate`);

            // Clear forms cache to ensure fresh data after activation
            this.clearFormsCache();

            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to activate form"
            };
        }
    }

    /**
     * Deactivate form
     */
    async deactivateForm(formId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/forms/${formId}/deactivate`);

            // Clear forms cache to ensure fresh data after deactivation
            this.clearFormsCache();

            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to deactivate form"
            };
        }
    }

    /**
     * Get unified field map for form (all versions)
     * 🗺️ FIELD MAP: Cached cross-version field ID resolution
     */
    async getFormFieldMap(formId, forceRefresh = false) {
        const cacheKey = `field_map_${formId}`;

        // Check cache first
        if (!forceRefresh && this.fieldMapCache.has(cacheKey)) {
            const cached = this.fieldMapCache.get(cacheKey);
            const now = Date.now();

            // Return cached if not expired
            if (now - cached.timestamp < this.fieldMapCacheTTL) {
                console.log(`✅ Field map cache HIT for form ${formId}`);
                return cached.data;
            } else {
                console.log(`⏰ Field map cache EXPIRED for form ${formId}`);
            }
        }

        try {
            console.log(`🔄 Fetching field map for form ${formId}...`);
            const response = await apiClient.get(`${this.baseURL}/forms/${formId}/field-map`);
            const result = unwrap(response);

            // Extract the actual field map data
            const fieldMapData = result.data || result;

            // Validate we have the expected structure
            if (!fieldMapData || !fieldMapData.field_map) {
                console.error(`❌ Invalid field map response structure:`, fieldMapData);
                throw new Error('Invalid field map response structure');
            }

            // Cache the result
            this.fieldMapCache.set(cacheKey, {
                data: fieldMapData,
                timestamp: Date.now(),
                cacheKey: fieldMapData.cache_key || `form_${formId}_field_map`
            });

            console.log(`✅ Field map cached for form ${formId} (${fieldMapData.version_count} versions, ${Object.keys(fieldMapData.field_map).length} unique fields)`);
            return fieldMapData;

        } catch (error) {
            console.error(`❌ Failed to fetch field map for form ${formId}:`, error);

            // Graceful degradation: return stale cache if available
            if (this.fieldMapCache.has(cacheKey)) {
                console.warn(`⚠️  Using STALE cache for form ${formId}`);
                return this.fieldMapCache.get(cacheKey).data;
            }

            // No cache available - return error response
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch field map",
                data: null
            };
        }
    }

    /**
     * Invalidate field map cache (call on form update)
     * 🗑️ CACHE INVALIDATION: Clear cached field map
     */
    invalidateFieldMapCache(formId) {
        const cacheKey = `field_map_${formId}`;

        if (this.fieldMapCache.has(cacheKey)) {
            this.fieldMapCache.delete(cacheKey);
            console.log(`🗑️  Invalidated field map cache for form ${formId}`);
            return true;
        }

        return false;
    }

    /**
     * Resolve field metadata from field_id using cached field map
     * 🔍 FIELD RESOLUTION: Get field info from field_id
     */
    resolveField(fieldId, fieldMap) {
        const fieldInfo = fieldMap.field_map[fieldId];

        if (!fieldInfo) {
            // Unknown field - return graceful fallback
            return {
                field_id: fieldId,
                label: `Unknown Field (${fieldId.substring(0, 8)}...)`,
                field_type: "unknown",
                versions: [],
                is_removed: true,
                current_label: null
            };
        }

        return fieldInfo;
    }

    /**
     * Batch resolve multiple field IDs
     * 🔍 BATCH RESOLUTION: Get multiple field infos at once
     */
    resolveFields(fieldIds, fieldMap) {
        return fieldIds.map(fieldId => this.resolveField(fieldId, fieldMap));
    }

    /**
     * Submit form data with PHI compliance validation
     * ✅ HEALTHCARE COMPLIANCE: Enhanced form submission with PHI awareness
     */
    async submitForm(formId, submissionData, formDefinition = null) {
        try {
            // Client-side PHI compliance check
            if (formDefinition && submissionData.data) {
                const complianceCheck = this.validatePhiCompliance(submissionData.data, formDefinition);
                
                if (complianceCheck.hasPhiFields) {
                    console.info('🔐 HIPAA COMPLIANCE: Form contains PHI fields - data will be encrypted by backend', {
                        phiFields: complianceCheck.phiFields,
                        warnings: complianceCheck.warnings
                    });
                }
            }
            
            // Add compliance metadata to submission
            const enhancedSubmissionData = {
                ...submissionData,
                compliance_metadata: {
                    phi_fields_detected: formDefinition ? 
                        this.validatePhiCompliance(submissionData.data || [], formDefinition).phiFields : [],
                    client_validation_timestamp: new Date().toISOString(),
                    encryption_awareness: true
                }
            };
            
            const response = await apiClient.post(`${this.baseURL}/forms/${formId}/submit`, enhancedSubmissionData);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to submit form",
                data: null
            };
        }
    }

    /**
     * Validate form data
     */
    async validateForm(formId, data) {
        try {
            const response = await apiClient.post(`${this.baseURL}/forms/${formId}/validate`, data);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Form validation failed",
                data: null
            };
        }
    }

    // ============================================================================
    // FORM SUBMISSIONS
    // ============================================================================

    /**
     * Get form submissions
     */
    async getFormSubmissions(formId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/submissions`, {
                params: { form_id: formId, ...params }
            });
            const { success, data, meta, message } = unwrap(response);
            const list = data?.items || data || [];
            const total = meta?.total || data?.total || 0;
            return { success, data: list, total, message };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form submissions",
                data: []
            };
        }
    }

    /**
     * Get specific form submission
     */
    async getFormSubmission(submissionId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/submissions/${submissionId}`);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form submission",
                data: null
            };
        }
    }

    // ============================================================================
    // DEPARTMENT MANAGEMENT
    // ============================================================================

    /**
     * Get departments with filtering
     */
    async getDepartments(params = {}) {
        try {
            // 🚀 PERFORMANCE OPTIMIZATION: Check cache first
            const cacheKey = `departments_${JSON.stringify(params)}`;
            const cached = this.getCachedData(cacheKey);
            if (cached) {
                console.log('🚀 Using cached departments data');
                return cached;
            }

            // 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending
            if (this.isRequestPending(cacheKey)) {
                console.log('🚀 Departments request already pending, waiting...');
                return await this.pendingRequests.get(cacheKey);
            }

            const promise = apiClient.get(`${this.baseURL}/departments`, { params })
                .then(response => {
                    const { success, data, meta, message } = unwrap(response);
                    const list = data?.items || data || [];
                    const total = meta?.total || data?.total || 0;
                    const result = { success, data: list, total, message };
                    this.setCachedData(cacheKey, result);
                    return result;
                });

            return await this.setRequestPending(cacheKey, promise);
        } catch (error) {
            console.error('Error fetching departments:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch departments",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get department by ID
     */
    async getDepartmentById(departmentId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/departments/${departmentId}`);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch department",
                data: null
            };
        }
    }

    /**
     * Create department
     */
    async createDepartment(departmentData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/departments`, departmentData);
            return {
                success: true,
                data: response.data,
                message: "Department created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create department",
                data: null
            };
        }
    }

    /**
     * Update department
     */
    async updateDepartment(departmentId, departmentData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/departments/${departmentId}`, departmentData);
            return {
                success: true,
                data: response.data,
                message: "Department updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update department",
                data: null
            };
        }
    }

    /**
     * Delete department
     */
    async deleteDepartment(departmentId) {
        try {
            await apiClient.delete(`${this.baseURL}/departments/${departmentId}`);
            return {
                success: true,
                message: "Department deleted successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to delete department"
            };
        }
    }

    // ============================================================================
    // CATEGORY MANAGEMENT
    // ============================================================================

    /**
     * Get categories with filtering
     */
    async getCategories(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/categories`, { params });

            // Transform the response to match expected format
            const data = response.data.items || response.data || [];
            const total = response.data.total || 0;

            return {
                success: true,
                data: data,
                total: total,
                message: "Categories fetched successfully"
            };
        } catch (error) {
            console.error('Error fetching categories:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch categories",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get category by ID
     */
    async getCategoryById(categoryId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/categories/${categoryId}`);
            return {
                success: true,
                data: response.data,
                message: "Category fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch category",
                data: null
            };
        }
    }

    /**
     * Create category
     */
    async createCategory(categoryData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/categories`, categoryData);
            return {
                success: true,
                data: response.data,
                message: "Category created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create category",
                data: null
            };
        }
    }

    /**
     * Update category
     */
    async updateCategory(categoryId, categoryData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/categories/${categoryId}`, categoryData);
            return {
                success: true,
                data: response.data,
                message: "Category updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update category",
                data: null
            };
        }
    }

    /**
     * Delete category
     */
    async deleteCategory(categoryId) {
        try {
            await apiClient.delete(`${this.baseURL}/categories/${categoryId}`);
            return {
                success: true,
                message: "Category deleted successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to delete category"
            };
        }
    }

    // ============================================================================
    // FOLDER MANAGEMENT
    // ============================================================================

    /**
     * Get folders with filtering and pagination
     */
    async getFolders(params = {}) {
        try {
            // 🚀 PERFORMANCE OPTIMIZATION: Check cache first
            const cacheKey = `folders_${JSON.stringify(params)}`;
            const cached = this.getCachedData(cacheKey);
            if (cached) {
                console.log('🚀 Using cached folders data');
                return cached;
            }

            // 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending
            if (this.isRequestPending(cacheKey)) {
                console.log('🚀 Folders request already pending, waiting...');
                return await this.pendingRequests.get(cacheKey);
            }

            const promise = apiClient.get(`${this.baseURL}/folders`, { params })
                .then(response => {
                    const { success, data, meta, message } = unwrap(response);
                    const list = data?.items || data || [];
                    const total = meta?.total || data?.total || 0;
                    const result = { success, data: list, total, message };
                    this.setCachedData(cacheKey, result);
                    return result;
                });

            return await this.setRequestPending(cacheKey, promise);
        } catch (error) {
            console.error('Error fetching folders:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch folders",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get folder by ID
     */
    async getFolderById(folderId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/folders/${folderId}`);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch folder",
                data: null
            };
        }
    }

    /**
     * Create folder
     */
    async createFolder(folderData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/folders`, folderData);
            
            // Clear cache to ensure fresh data after creating folder
            this.clearCache();
            
            return {
                success: true,
                data: response.data,
                message: "Folder created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create folder"
            };
        }
    }

    /**
     * Update folder
     */
    async updateFolder(folderId, folderData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/folders/${folderId}`, folderData);
            
            // Clear cache to ensure fresh data after updating folder
            this.clearCache();
            
            return {
                success: true,
                data: response.data,
                message: "Folder updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update folder"
            };
        }
    }

    /**
     * Delete folder
     */
    async deleteFolder(folderId) {
        try {
            await apiClient.delete(`${this.baseURL}/folders/${folderId}`);
            
            // Clear cache to ensure fresh data after deleting folder
            this.clearCache();
            
            return {
                success: true,
                message: "Folder deleted successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to delete folder"
            };
        }
    }

    /**
     * Get templates with filtering
     */
    async getTemplates(params = {}) {
        try {
            // 🚀 PERFORMANCE OPTIMIZATION: Check cache first
            const cacheKey = `templates_${JSON.stringify(params)}`;
            const cached = this.getCachedData(cacheKey);
            if (cached) {
                console.log('🚀 Using cached templates data');
                return cached;
            }

            // 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending
            if (this.isRequestPending(cacheKey)) {
                console.log('🚀 Templates request already pending, waiting...');
                return await this.pendingRequests.get(cacheKey);
            }

            const promise = apiClient.get(`${this.baseURL}/templates`, { params })
                .then(response => {
                    const { success, data, meta, message } = unwrap(response);
                    const list = data?.items || data || [];
                    const total = meta?.total || data?.total || 0;
                    const result = { success, data: list, total, message };
                    this.setCachedData(cacheKey, result);
                    return result;
                });

            return await this.setRequestPending(cacheKey, promise);
        } catch (error) {
            console.error('Error fetching templates:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch templates",
                data: [],
                total: 0
            };
        }
    }

    /**
     * 🚀 PERFORMANCE OPTIMIZATION: Batch load all navigation data at once
     * This reduces multiple API calls to a single batch request
     */
    async batchLoadNavigationData() {
        try {
            console.log('🚀 Batch loading navigation data...');
            
            const batchRequests = {
                forms: () => this.getForms({ summary: true, include_details: true }),
                folders: () => this.getFolders(),
                departments: () => this.getDepartments()
                // Removed templates: () => this.getTemplates() to eliminate 404 API call
            };

            const results = await this.batchRequests(batchRequests);
            
            console.log('🚀 Navigation data batch loaded successfully');
            return results;
        } catch (error) {
            console.error('Error batch loading navigation data:', error);
            return {
                forms: { success: false, data: [], total: 0 },
                folders: { success: false, data: [], total: 0 },
                departments: { success: false, data: [], total: 0 }
                // Removed templates error handling since we're no longer calling it
            };
        }
    }

    /**
     * Move forms to folder
     */
    async moveFormsToFolder(folderId, formIds) {
        try {
            const response = await apiClient.post(`${this.baseURL}/folders/${folderId}/move-forms`, formIds);
            
            // Clear forms cache to ensure fresh data after moving forms
            this.clearFormsCache();
            
            return {
                success: true,
                data: response.data,
                message: "Forms moved to folder successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to move forms to folder"
            };
        }
    }

    /**
     * Remove forms from folders
     */
    async removeFormsFromFolders(formIds) {
        try {
            const response = await apiClient.post(`${this.baseURL}/folders/remove-forms`, formIds);
            
            // Clear forms cache to ensure fresh data after removing forms from folders
            this.clearFormsCache();
            
            return {
                success: true,
                data: response.data,
                message: "Forms removed from folders successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to remove forms from folders"
            };
        }
    }

    // ============================================================================
    // UTILITY ENDPOINTS
    // ============================================================================

    /**
     * Get available field types
     */
    async getFieldTypes() {
        try {
            const response = await apiClient.get(`${this.baseURL}/field-types`);
            return {
                success: true,
                data: response.data,
                message: "Field types fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch field types",
                data: []
            };
        }
    }

    /**
     * Get form types by department
     */
    async getFormTypes(departmentId = null) {
        try {
            const params = departmentId ? { department_id: departmentId } : {};
            const response = await apiClient.get(`${this.baseURL}/form-types`, { params });
            return {
                success: true,
                data: response.data,
                message: "Form types fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form types",
                data: []
            };
        }
    }

    // ============================================================================
    // ANALYTICS
    // ============================================================================

    /**
     * Get submission statistics
     */
    async getSubmissionStats(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/submission-stats`, { params });
            return {
                success: true,
                data: response.data,
                message: "Submission statistics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch submission statistics",
                data: null
            };
        }
    }

    /**
     * Get form submission counts
     */
    async getFormSubmissionCounts(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/form-submission-counts`, { params });
            return {
                success: true,
                data: response.data,
                message: "Form submission counts fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch form submission counts",
                data: null
            };
        }
    }

    /**
     * Get monthly submission statistics
     */
    async getMonthlyStats(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/monthly-stats`, { params });
            return {
                success: true,
                data: response.data,
                message: "Monthly statistics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch monthly statistics",
                data: null
            };
        }
    }

    // ============================================================================
    // DRAFT OPERATIONS
    // ============================================================================

    /**
     * Save form as draft
     */
    async saveDraft(formId, draftData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/forms/${formId}/drafts`, draftData);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to save draft",
                data: null
            };
        }
    }

    /**
     * Update existing draft
     */
    async updateDraft(formId, draftId, draftData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/forms/${formId}/drafts/${draftId}`, draftData);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update draft",
                data: null
            };
        }
    }

    /**
     * Submit draft as final submission
     */
    async submitDraft(formId, draftId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/forms/${formId}/drafts/${draftId}/submit`);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to submit draft",
                data: null
            };
        }
    }

    /**
     * Get drafts for a form
     */
    async getFormDrafts(formId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/forms/${formId}/drafts`, { params });
            const { success, data, meta, message } = unwrap(response);
            const list = data?.items || data || [];
            const total = meta?.total || data?.total || 0;
            return { success, data: list, total, message };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch drafts",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get submission history for a patient and form
     */
    async getSubmissionHistory(formId, patientId) {
        try {
            // 🚀 PERFORMANCE OPTIMIZATION: Check cache first
            const cacheKey = `submissions_${formId}_${patientId}`;
            const cached = this.getCachedData(cacheKey);
            if (cached) {
                console.log('🚀 Using cached submission history for:', formId, patientId);
                return cached;
            }

            // 🚀 PERFORMANCE OPTIMIZATION: Check if request is already pending (local and global)
            if (this.isRequestPending(cacheKey)) {
                console.log('🚀 Submission history request already pending locally for:', formId, patientId);
                return await this.pendingRequests.get(cacheKey);
            }
            
            if (DynamicFormsService.isGlobalRequestPending(cacheKey)) {
                console.log('🚀 Submission history request already pending globally for:', formId, patientId);
                return await DynamicFormsService.globalPendingRequests.get(cacheKey);
            }

            const promise = apiClient.get(`${this.baseURL}/submissions/${formId}/patient/${patientId}`)
                .then(response => {
                    const result = unwrap(response);
                    this.setCachedData(cacheKey, result);
                    return result;
                });

            // Set both local and global pending requests
            this.setRequestPending(cacheKey, promise);
            return await DynamicFormsService.setGlobalRequestPending(cacheKey, promise);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch submission history",
                data: null
            };
        }
    }

    /**
     * Update an existing submission (creates new version)
     */
    async updateSubmission(submissionId, submissionData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/submissions/${submissionId}`, submissionData);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update submission",
                data: null
            };
        }
    }

    /**
     * Get draft by ID
     */
    async getDraftById(formId, draftId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/forms/${formId}/drafts/${draftId}`);
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to get draft",
                data: null
            };
        }
    }

    /**
     * Get current draft for patient and form (utility method)
     */
    async getCurrentDraft(formId, patientId) {
        try {
            const response = await this.getFormDrafts(formId, { 
                patient_id: patientId, 
                limit: 1 
            });
            
            if (response.success && response.data && response.data.length > 0) {
                return {
                    success: true,
                    data: response.data[0],
                    message: "Draft found"
                };
            } else {
                return {
                    success: true,
                    data: null,
                    message: "No draft found"
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to get current draft",
                data: null
            };
        }
    }

    /**
     * Get all drafts for a specific patient across all forms
     */
    async getPatientDrafts(patientId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/submissions`, {
                params: { 
                    patient_id: patientId,
                    is_complete: false,
                    limit: 50
                }
            });
            const { success, data, meta, message } = unwrap(response);
            const list = data?.items || data || [];
            const total = meta?.total || data?.total || 0;
            return { success, data: list, total, message };
        } catch (error) {
            console.error('Error fetching patient drafts:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch patient drafts",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get recent submissions for a specific patient across all forms
     */
    async getPatientRecentSubmissions(patientId, limit = 20) {
        try {
            const response = await apiClient.get(`${this.baseURL}/submissions`, {
                params: { 
                    patient_id: patientId,
                    is_complete: true,
                    limit: limit,
                    // Sort by newest first
                    sort: '-submitted_at'
                }
            });
            const { success, data, meta, message } = unwrap(response);
            const list = data?.items || data || [];
            const total = meta?.total || data?.total || 0;
            return { success, data: list, total, message };
        } catch (error) {
            console.error('Error fetching patient recent submissions:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch patient recent submissions",
                data: [],
                total: 0
            };
        }
    }

    /**
     * Get all submissions for a specific patient (optimized single query)
     * @param {string} patientId - Patient ID
     * @param {number} limit - Maximum number of submissions to fetch
     * @returns {Promise<Object>} All submissions for the patient
     */
    async getPatientSubmissions(patientId, limit = 1000) {
        try {
            const response = await apiClient.get(`/dynamic-forms/patients/${patientId}/submissions`, {
                params: {
                    limit: limit,
                    sort_by: 'submitted_at',
                    sort_order: 'desc'
                },
                timeout: 1200000  // 2 minutes timeout for large submission lists
            });
            
            return {
                success: true,
                data: response.data,
                submissions: response.data?.submissions || response.data?.items || [],
                total: response.data?.total || 0
            };
        } catch (error) {
            console.error('Error fetching patient submissions:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch patient submissions",
                data: [],
                submissions: [],
                total: 0
            };
        }
    }

    /**
     * Use submission data as template for new submission
     */
    async useSubmissionAsTemplate(submissionId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/submissions/${submissionId}/use-as-template`);
            const { success, template_data, message } = unwrap(response);
            return { success, template_data, message };
        } catch (error) {
            console.error('Error using submission as template:', error);
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to use submission as template",
                template_data: null
            };
        }
    }


}

const dynamicFormsService = new DynamicFormsService();

export default dynamicFormsService; 